import CardWrapper from '@/components/common/CardWrapper/CardWrapper';
import { stepOneSchema, stepTwoSchema } from '@/components/common/UpdateProfile/Schemas';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CustomInput } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LocationAutoComplete } from '@/components/ui/LocationAutocomplete';
import { SelectComponent } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { LANGUAGE } from '@/lib/constants';
import { ageRangeOptions, cmToFeet, feetToCm, kgToLbs, lbsToKg } from '@/lib/utils';
import { Gender, HeightType, WeightType } from '@/Types/gql/graphql';
import { round } from 'lodash';
import { CameraIcon, UserRound, X } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
// import { UserSchemaType } from '../signup';
const UserSchema = stepOneSchema.merge(stepTwoSchema);

export type UserSchemaType = z.infer<typeof UserSchema>;
interface SignupStepOneProps {
  croppedImage: File | undefined;
  handleImageClick: () => void;
  imageRef: React.RefObject<HTMLInputElement>;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleNavigateLogin?: () => void;
  handleNextStep?: () => void;
  isEdit?: boolean;
  profileImageUrl?: string;
}

const SiteUrl = import.meta.env.VITE_SITE_URL;

const SignupStepOne: React.FC<SignupStepOneProps> = ({
  croppedImage,
  handleImageClick,
  imageRef,
  handleImageChange,
  handleNavigateLogin,
  handleNextStep,
  isEdit,
  profileImageUrl,
}) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<UserSchemaType>();
  const selectAgeRange = (text: string) => {
    setValue('ageRange', text, {
      shouldValidate: true,
    });
  };
  const heightType = watch('heightType');
  const weightType = watch('weightType');
  const selectHeightType = (text: string) => {
    setValue('heightType', text, {
      shouldValidate: true,
    });
  };
  const selectWeightType = (text: string) => {
    setValue('weightType', text, {
      shouldValidate: true,
    });
  };

  const handleHeightChange = (value: number[]) => {
    const convertedValue = heightType === HeightType.Centimeter ? value[0] : feetToCm(value[0]);
    setValue('height', convertedValue, { shouldValidate: true });
  };

  const handleWeightChange = (value: number[]) => {
    const convertedValue = weightType === WeightType.Kilogram ? value[0] : lbsToKg(value[0]);
    setValue('weight', convertedValue, { shouldValidate: true });
  };
  return (
    <Tabs className='w-full flex flex-col items-center' defaultValue='customer'>
      {isEdit ? (
        ''
      ) : (
        <TabsList className='h-12 grid w-full grid-cols-2 bg-[#ffffff29] rounded-full p-1 max-w-[350px]'>
          <TabsTrigger
            className='rounded-full h-10 px-4 py-2 text-xs data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:font-semibold data-[state=active]:shadow-sm'
            value='customer'
          >
            Customer Account
          </TabsTrigger>
          <TabsTrigger
            className='rounded-full h-10 px-4 py-2 text-xs data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:font-semibold data-[state=active]:shadow-sm'
            value='trainer'
          >
            Trainer Account
          </TabsTrigger>
        </TabsList>
      )}
      <TabsContent value='customer' className='w-full mt-8 flex flex-col gap-4'>
        <div className='w-full rounded-[20px] p-5 bg-[linear-gradient(154.36deg,rgba(47,47,47,0.44)_11.3%,rgba(47,47,47,0.44)_95.38%)]'>
          <h4>Personal Information</h4>
          <div className='flex justify-center'>
            <div className='relative w-min m-5'>
              <div className=' flex items-center justify-center h-[100px] w-[100px] rounded-full bg-slate-600 overflow-hidden'>
                <Avatar className='w-full h-full'>
                  <AvatarImage
                    src={
                      profileImageUrl
                        ? profileImageUrl
                        : croppedImage
                          ? URL.createObjectURL(croppedImage as Blob)
                          : ''
                    }
                    alt='avatar'
                    className='rounded-full w-full '
                  />
                  <AvatarFallback className='flex  items-center justify-center'>
                    <UserRound color='#fff' size={44} />
                  </AvatarFallback>
                </Avatar>
                <Button
                  id='image-upload-btn'
                  variant='ghost'
                  className='flex items-center justify-center w-8 h-8 absolute top-[-5px] right-0 rounded-full p-0 bg-[#343840] text-white'
                  type='button'
                  onClick={handleImageClick}
                >
                  {profileImageUrl || croppedImage ? <X className='w-5 h-5' /> : <CameraIcon />}
                </Button>
                <input
                  type='file'
                  className='hidden'
                  accept='.jpg, .png, .jpeg, .webp'
                  id='admin-profile-image'
                  data-testid='image-upload'
                  ref={imageRef}
                  onChange={handleImageChange}
                />
              </div>
            </div>
          </div>
          <div className='grid grid-cols-2 gap-5'>
            <CustomInput
              id='create-acc-fname-id'
              {...register('firstName')}
              label='First Name'
              className='rounded-xl'
              error={errors?.firstName?.message}
              requiredItem
            />
            <CustomInput
              id='create-acc-fname-id'
              {...register('lastName')}
              label='Last Name'
              className='rounded-xl'
              error={errors?.lastName?.message}
              requiredItem
            />
            <CustomInput
              id='create-acc-fname-id'
              {...register('email')}
              label='Email'
              className='rounded-xl'
              error={errors?.email?.message}
              requiredItem
            />
            {/* <DatePicker
              placeholder='DOB'
              format='MMMM DD, YYYY'
              selectedDate={watch('dateOfBirth') ? new Date(watch('dateOfBirth')) : undefined}
              onChange={(val: Date | undefined) => {
                const utcDate = dayjs(val).utc().toDate(); // Convert to UTC using dayjs
                setValue('dateOfBirth', utcDate.toUTCString(), { shouldValidate: true });
              }}
              onClear={() => {
                setValue('dateOfBirth', '', { shouldValidate: true });
              }}
              requiredItem
              error={errors.dateOfBirth?.message}
            /> */}
            <SelectComponent
              id='age-range'
              values={ageRangeOptions}
              label='Age Range'
              value={watch('ageRange')}
              setValue={selectAgeRange}
              className='rounded-xl'
              size='md'
              clear={false}
              error={errors.ageRange?.message}
            />
            {isEdit ? (
              <div className='col-span-2'>
                <LocationAutoComplete
                  onSelection={() => {}}
                  onValueChange={(value) =>
                    setValue('location', value.label, { shouldValidate: true })
                  }
                  setCountry={(country) => setValue('country', country)}
                  label='Location'
                  iValue={watch('location')}
                  searchIcon={false}
                  error={errors?.location?.message}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className='w-full flex flex-col gap-4 rounded-[20px] p-5 bg-[linear-gradient(154.36deg,rgba(47,47,47,0.44)_11.3%,rgba(47,47,47,0.44)_95.38%)]'>
          <h4>Physical Attributes</h4>
          <div className='space-y-2'>
            <Label htmlFor='gender' className='text-sm font-medium text-zinc-400'>
              Select Gender<span className='text-[#DC1D00]'>*</span>
            </Label>
            <ToggleGroup
              type='single'
              value={watch('gender')}
              onValueChange={(value) =>
                value && setValue('gender', value, { shouldValidate: true })
              }
              className='grid grid-cols-3 gap-2'
            >
              <ToggleGroupItem
                value={Gender.Male}
                className='bg-zinc-700 data-[state=on]:bg-white data-[state=on]:text-black'
              >
                Male
              </ToggleGroupItem>
              <ToggleGroupItem
                value={Gender.Female}
                className='bg-zinc-700 data-[state=on]:bg-white data-[state=on]:text-black'
              >
                Female
              </ToggleGroupItem>
              <ToggleGroupItem
                value={Gender.NonBinary}
                className='bg-zinc-700 data-[state=on]:bg-white data-[state=on]:text-black'
              >
                Non-Binary
              </ToggleGroupItem>
            </ToggleGroup>
            {errors?.gender?.message && (
              <p
                aria-label='error'
                aria-errormessage={errors.gender?.message}
                className='text-red text-xs'
              >
                {errors?.gender?.message}
              </p>
            )}
          </div>
          {isEdit && (
            <>
              <div className='space-y-2'>
                <Label htmlFor='height' className='text-sm font-medium text-zinc-400'>
                  Your Height
                </Label>
                <div className='flex items-center space-x-4'>
                  <div className='w-20'>
                    <SelectComponent
                      id='height-type-select'
                      values={[
                        { text: 'cm', value: HeightType.Centimeter },
                        { text: 'ft', value: HeightType.Inch },
                      ]}
                      value={heightType}
                      setValue={selectHeightType}
                      className='rounded-xl'
                      hideLabel={true}
                      size='sm'
                      clear={false}
                    />
                  </div>
                  <Slider
                    id='height'
                    min={0}
                    max={heightType === HeightType.Centimeter ? 275 : 9}
                    step={0.1}
                    value={[
                      heightType === HeightType.Centimeter
                        ? watch('height') || 0
                        : cmToFeet(watch('height') || 0),
                    ]}
                    onValueChange={handleHeightChange}
                    className='flex-1'
                  />
                  <span className='min-w-[4ch] text-right w-20'>
                    {heightType === HeightType.Centimeter
                      ? round(watch('height') ?? 0, 1)
                      : round(cmToFeet(watch('height') ?? 0), 1)}{' '}
                    {heightType === HeightType.Centimeter ? 'cm' : 'ft'}
                  </span>
                </div>
                {errors?.height?.message && (
                  <p
                    aria-label='error'
                    aria-errormessage={errors.height?.message}
                    className='text-red text-xs'
                  >
                    {errors?.height?.message}
                  </p>
                )}
              </div>
              <div className='space-y-2'>
                <Label htmlFor='weight' className='text-sm font-medium text-zinc-400'>
                  Your Weight
                </Label>
                <div className='flex items-center space-x-4'>
                  <div className='w-20'>
                    <SelectComponent
                      id='height-type-select'
                      values={[
                        { text: 'kg', value: WeightType.Kilogram },
                        { text: 'lbs', value: WeightType.Pound },
                      ]}
                      value={weightType}
                      setValue={selectWeightType}
                      className='rounded-xl'
                      hideLabel={true}
                      size='sm'
                      clear={false}
                    />
                  </div>
                  <Slider
                    id='height'
                    min={0}
                    max={weightType === WeightType.Kilogram ? 499 : kgToLbs(499)}
                    step={1}
                    value={[
                      weightType === WeightType.Kilogram
                        ? (watch('weight') ?? 0)
                        : kgToLbs(watch('weight') ?? 0),
                    ]}
                    onValueChange={handleWeightChange}
                    className='flex-1'
                  />
                  <span className='min-w-[4ch] text-right w-20'>
                    {weightType === WeightType.Kilogram
                      ? round(watch('weight') ?? 0, 1)
                      : round(kgToLbs(watch('weight') ?? 0), 1)}{' '}
                    {weightType === WeightType.Kilogram ? 'kg' : 'lbs'}
                  </span>
                </div>
                {errors?.weight?.message && (
                  <p
                    aria-label='error'
                    aria-errormessage={errors.weight?.message}
                    className='text-red text-xs'
                  >
                    {errors?.weight?.message}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <CardWrapper>
          <div className='space-y-4'>
            <Label>
              Preferred Languages
              <p className='text-textSecondary font-normal mt-1'>
                (You can select multiple items)<span className='text-red'>*</span>
              </p>
            </Label>
            <ToggleGroup
              value={watch('languages')}
              onValueChange={(value) => setValue('languages', value, { shouldValidate: true })}
              type='multiple'
              className='grid grid-cols-4 gap-2'
            >
              {LANGUAGE.map((language) => (
                <ToggleGroupItem key={language.value} value={language.value}>
                  {language.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {errors?.languages?.message && (
              <p
                aria-label='error'
                aria-errormessage={errors?.languages?.message}
                className='text-red text-xs'
              >
                {errors?.languages?.message}
              </p>
            )}
          </div>
        </CardWrapper>

        {!isEdit && (
          <>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='terms'
                checked={watch('terms')}
                onCheckedChange={(checked: any) =>
                  setValue('terms', checked as boolean, { shouldValidate: true })
                }
              />
              <label
                htmlFor='terms'
                className='text-sm font-medium leading-5 peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
              >
                I have read and agreed to the{' '}
                <a href={SiteUrl + 'terms-and-conditions'} target='_blank' rel='noreferrer'>
                  <b>Terms & Conditions</b>
                </a>
                {', '}
                <a href={SiteUrl + 'privacy-policy'} target='_blank' rel='noreferrer'>
                  <b>Privacy Policy</b>
                </a>{' '}
                and{' '}
                <a
                  href={SiteUrl + 'terms-and-conditions/#h-17-limitation-of-liability'}
                  target='_blank'
                  rel='noreferrer'
                >
                  <b>Waiver of Liabilities</b>
                </a>
              </label>
            </div>

            {errors?.terms?.message && (
              <p
                aria-label='error'
                aria-errormessage={errors?.terms?.message}
                className='text-red text-xs'
              >
                {errors?.terms?.message}
              </p>
            )}
          </>
        )}

        {handleNextStep && (
          <Button
            id='create-account-btn'
            size='lg'
            className='w-full mt-2'
            onClick={() => handleNextStep()}
          >
            Next
          </Button>
        )}
        {handleNavigateLogin && (
          <h5 className='font-normal mt-2 text-center'>
            Entered the wrong number?{' '}
            <Button
              id='resend-btn'
              onClick={handleNavigateLogin}
              variant='ghost'
              className='p-0 w-min h-min'
            >
              <b>Edit Phone Number.</b>
            </Button>
          </h5>
        )}
      </TabsContent>
      <TabsContent value='trainer'>
        <h5 className='font-normal'>
          Currently, we do a manual review one-on-one to assess trainer qualifications and platform
          requirements. Please send your resume to{' '}
          <b>
            <a href='mailto:info@fitstatic.com'>info@fitstatic.com</a>
          </b>{' '}
          or call us at <br />
          <b>
            <a href='tel:+1-646-887-2690'>+1 (646) 887-2690</a>
          </b>{' '}
          and one of our specialists will onboard you into our platform.
        </h5>
      </TabsContent>
    </Tabs>
  );
};

export default SignupStepOne;
